/**
 * Entering a price for this card and grade, and the result.
 *
 * The grade comes from the Market value switch above it, so what is evaluated is what is
 * on screen. Raw cards ask for the condition, one tap; PSA 10 does not.
 */

import { type FormEvent, useEffect, useState } from "react";
import { Scale } from "lucide-react";
import { CONDITION_LABEL, CONDITIONS, type Condition, type Place } from "@ohc-price/shared";
import { ApiError, type Evaluation, evaluateApi, type ValuedGrade } from "./api";
import { EvaluationView } from "./EvaluationView";
import { Callout, cx, Segmented, Spinner } from "./ui";

const PLACES = [
  { value: "show", label: "Trade show" },
  { value: "online", label: "Online" },
] as const;

const KINDS = [
  { value: "asking", label: "Their asking price" },
  { value: "offer", label: "Our offer" },
] as const;

export function Evaluate({ cardId, grade, onError }: { cardId: number; grade: ValuedGrade; onError: (e: unknown) => void }) {
  const [condition, setCondition] = useState<Condition>("NM");
  const [priceKind, setPriceKind] = useState<"asking" | "offer">("asking");
  const [place, setPlace] = useState<Place>("show");
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<Evaluation | null>(null);

  // What is happening while it works: the sales are read, then the AI is asked.
  useEffect(() => {
    if (!busy) return;
    setStep("Checking the sales");
    const timer = setTimeout(() => setStep("Asking the AI"), 2500);
    return () => clearTimeout(timer);
  }, [busy]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await evaluateApi.evaluate({
        cardId,
        grade,
        condition: grade === "ungraded" ? condition : null,
        place,
        priceKind,
        price: price.trim().replace(/^S?\$/i, "").replace(/,/g, ""),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setResult(r.evaluation);
    } catch (e) {
      onError(e);
      setError(e instanceof ApiError ? e.message : "The evaluation did not go through. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3">
      <form onSubmit={submit} className="card p-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="label">Evaluate a price</div>
            <div className="mt-0.5 text-xs text-slate-500">{grade === "psa10" ? "PSA 10 slab" : "Raw card"}, in Singapore dollars</div>
          </div>
          <Scale className="h-5 w-5 text-slate-300" />
        </div>

        <div className="space-y-2">
          <Segmented full value={place} options={PLACES} onChange={setPlace} />
          <Segmented full value={priceKind} options={KINDS} onChange={setPriceKind} />
        </div>

        <label className="mt-3 flex h-16 items-center rounded-2xl border border-slate-300 bg-white px-4 transition focus-within:border-navy-600 focus-within:ring-4 focus-within:ring-navy-100">
          <span className="mr-2 text-lg font-medium text-slate-400">S$</span>
          <input
            inputMode="decimal"
            autoComplete="off"
            className="num w-full bg-transparent text-[28px] font-semibold tracking-tight text-slate-900 outline-none placeholder:text-slate-300"
            placeholder="0.00"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            aria-label="Price in Singapore dollars"
          />
        </label>

        {grade === "ungraded" && (
          <div className="mt-3">
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-[13px] font-medium text-slate-700">Condition</span>
              <span className="text-xs text-slate-500">{CONDITION_LABEL[condition]}</span>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {CONDITIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  title={CONDITION_LABEL[c]}
                  onClick={() => setCondition(c)}
                  className={cx(
                    "rounded-xl border py-2 text-[13px] font-semibold transition",
                    condition === c ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        <input
          className="field mt-3 h-11 text-sm"
          placeholder="Note, optional: who offered it, where"
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />

        {error && <div className="mt-3"><Callout kind="error">{error}</Callout></div>}

        <button type="submit" disabled={busy || !price.trim()} className="btn-gold mt-4 w-full text-[15px]">
          {busy ? (
            <>
              <Spinner />
              {step}
            </>
          ) : (
            "Evaluate"
          )}
        </button>
      </form>

      {result && (
        <div className="animate-fadein">
          <EvaluationView e={result} />
          <p className="mt-2 text-center text-xs text-slate-400">Saved to History</p>
        </div>
      )}
    </section>
  );
}
